interface PaginationSEOParams {
  baseUrl: string;
  currentPage: number;
  totalPages: number;
  baseTitle: string;
  searchParams?: URLSearchParams;
}

interface PaginationMetadata {
  title: string;
  canonical: string;
  prev?: string;
  next?: string;
}

export function generatePaginationMetadata({
  baseUrl,
  currentPage,
  totalPages,
  baseTitle,
  searchParams,
}: PaginationSEOParams): PaginationMetadata {
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  
  const params = searchParams ? new URLSearchParams(searchParams.toString()) : new URLSearchParams();
  
  const title = currentPage > 1 ? `${baseTitle} - Page ${currentPage}` : baseTitle;
  
  let canonical = cleanBaseUrl;
  if (currentPage > 1) {
    params.set('page', currentPage.toString());
  } else {
    params.delete('page');
  }
  const canonicalQuery = params.toString();
  if (canonicalQuery) {
    canonical += `?${canonicalQuery}`;
  }
  
  let prev: string | undefined;
  if (currentPage > 1) {
    const prevParams = new URLSearchParams(params.toString());
    if (currentPage === 2) {
      prevParams.delete('page');
    } else {
      prevParams.set('page', (currentPage - 1).toString());
    }
    const prevQuery = prevParams.toString();
    // Add trailing slash before query params to match canonical format
    prev = prevQuery ? `${cleanBaseUrl}/?${prevQuery}` : cleanBaseUrl;
  }
  
  // Generate next URL (if not on last page)
  let next: string | undefined;
  if (currentPage < totalPages) {
    const nextParams = new URLSearchParams(params.toString());
    nextParams.set('page', (currentPage + 1).toString());
    // Add trailing slash before query params to match canonical format
    next = `${cleanBaseUrl}/?${nextParams.toString()}`;
  }
  
  return {
    title,
    canonical,
    prev,
    next,
  };
}

export function generateNextJSPaginationMetadata(params: PaginationSEOParams) {
  const { title, canonical, prev, next } = generatePaginationMetadata(params);
  
  const metadata: {
    title: string;
    alternates: { canonical: string };
    other: Record<string, string>;
  } = {
    title,
    alternates: {
      canonical,
    },
    other: {},
  };
  
  // Add prev/next links for search engines
  if (prev) {
    metadata.other.prev = prev;
  }
  if (next) {
    metadata.other.next = next;
  }
  
  return metadata;
}

export function generateCrawlablePageLinks(
  baseUrl: string,
  totalPages: number,
  searchParams?: URLSearchParams
): string[] {
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  const links: string[] = [];
  
  for (let page = 1; page <= totalPages; page++) {
    const params = searchParams ? new URLSearchParams(searchParams.toString()) : new URLSearchParams();
    
    if (page === 1) {
      params.delete('page');
    } else {
      params.set('page', page.toString());
    }
    
    const query = params.toString();
    const url = query ? `${cleanBaseUrl}/?${query}` : cleanBaseUrl;
    links.push(url);
  }
  
  return links;
}

export function parsePageNumber(
  searchParams: URLSearchParams | { page?: string } | null | undefined,
  defaultPage: number = 1
): number {
  if (!searchParams) return defaultPage;
  
  const pageParam = searchParams instanceof URLSearchParams 
    ? searchParams.get('page')
    : searchParams.page;
    
  if (!pageParam) return defaultPage;
  
  const page = parseInt(pageParam, 10);
  
  if (isNaN(page) || page < 1) {
    return defaultPage;
  }
  
  return page;
}